import { useEffect, useRef, useState } from 'react'

/**
 * ドラッグ開始時点からのスクロール差分を追跡するフック。
 * 始点座標（clientX/clientY）はドラッグ開始時のビューポート座標で固定されるため、
 * スクロールが発生すると始点がグリッド内容に対してずれる。
 * このフックが返す差分を始点座標から減算することで、始点を内容に追従させる。
 */
export const useDragScrollOffset = (
  containerRef: React.RefObject<HTMLElement>,
  isDragging: boolean
): { x: number; y: number } => {
  const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 })
  const initialScrollRef = useRef({ top: 0, left: 0 })

  useEffect(() => {
    if (!isDragging) {
      setScrollOffset({ x: 0, y: 0 })
      return
    }

    const container = containerRef.current
    if (!container) return

    const viewport = container.querySelector('.ag-body-viewport')
    if (!viewport) return

    const hScrollViewport = container.querySelector(
      '.ag-body-horizontal-scroll-viewport'
    )
    const scrollTarget = hScrollViewport ?? viewport

    initialScrollRef.current = {
      top: viewport.scrollTop,
      left: scrollTarget.scrollLeft,
    }

    let rafId: number | null = null

    const handleScroll = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        const deltaX = scrollTarget.scrollLeft - initialScrollRef.current.left
        const deltaY = viewport.scrollTop - initialScrollRef.current.top
        setScrollOffset({ x: deltaX, y: deltaY })
      })
    }

    viewport.addEventListener('scroll', handleScroll, { passive: true })
    if (hScrollViewport && hScrollViewport !== viewport) {
      hScrollViewport.addEventListener('scroll', handleScroll, {
        passive: true,
      })
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      viewport.removeEventListener('scroll', handleScroll)
      if (hScrollViewport && hScrollViewport !== viewport) {
        hScrollViewport.removeEventListener('scroll', handleScroll)
      }
    }
  }, [containerRef, isDragging])

  return scrollOffset
}
